import { FC, useState } from 'react';
import type { ActiveRemediationRunSummary, Assessment } from '@libs/types';
import { Box, Button, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { DataTable, type DataTableColumn } from '@components/DataTable';
import { useTrans } from '@libs/i18n';
import { ClockCounterClockwiseIcon, FilesIcon } from '@phosphor-icons/react';

import { RemediatedDocumentsDialog } from './RemediatedDocumentsDialog';
import { ScoreBar } from './ScoreBar';

export interface RemediatedRow {
  assessment: Assessment;
  /** The newest run that actually produced documents. */
  run: ActiveRemediationRunSummary;
}

const TABLE_MIN_WIDTH = 860;

/**
 * Assessments that have been through remediation at least once.
 *
 * Split out of the main table rather than being another state in it, because it
 * answers a different question. That table asks "what still needs doing" — a
 * queue, where every row carries an action to clear it. Once an assessment has
 * been remediated the question becomes "what did I get", and the useful facts
 * are what came out: how many documents were created, how many were improved,
 * and where they are. Those were not columns the queue had, and adding them
 * would have left them blank on every row that had never been remediated.
 *
 * Only accepted work is counted — see `itemCounts.generated` / `.remediated`.
 * A rejected document is one the user read and did not keep, so it is not
 * something this assessment now has.
 */
export const RemediatedAssessmentsTable: FC<{
  rows: RemediatedRow[];
  language: string;
  isLoading?: boolean;
  onOpenAssessment: (assessmentId: string) => void;
}> = observer(({ rows, language, isLoading, onOpenAssessment }) => {
  const { t } = useTrans();
  const [openDocs, setOpenDocs] = useState<RemediatedRow | null>(null);

  const columns: DataTableColumn[] = [
    {
      key: 'assessment',
      label: t('app.remediation.remediated.col.assessment'),
      width: '32%',
      skeleton: 'title',
    },
    { key: 'lastRemediated', label: t('app.remediation.remediated.col.lastRemediated'), width: '15%' },
    { key: 'score', label: t('app.remediation.remediated.col.score'), width: '16%', skeleton: 'bar' },
    {
      key: 'improved',
      label: t('app.remediation.remediated.col.improved'),
      width: '10%',
      align: 'center',
      skeleton: 'number',
    },
    {
      key: 'created',
      label: t('app.remediation.remediated.col.created'),
      width: '10%',
      align: 'center',
      skeleton: 'number',
    },
    {
      key: 'documents',
      label: t('app.remediation.remediated.col.documents'),
      align: 'right',
      skeleton: 'action',
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        minWidth={TABLE_MIN_WIDTH}
        isLoading={isLoading}
        isEmpty={rows.length === 0}
        emptyLabel={t('app.remediation.remediated.empty')}
      >
        {rows.map(({ assessment, run }) => (
          <TableRow key={assessment.id} hover>
            <TableCell sx={{ pl: 2 }}>
              <Typography
                variant="textSM_regular"
                sx={{ fontWeight: 600, display: 'block', overflowWrap: 'anywhere' }}
              >
                {assessment.title}
              </Typography>
              <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                {t('app.remediation.remediated.versionLabel', { version: assessment.version })}
              </Typography>
            </TableCell>

            <TableCell>
              <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
                {/* The run's own last movement, which for a finished run is when
                    it was applied — the moment the documents came into being. */}
                {new Date(run.updatedAt).toLocaleDateString(language)}
              </Typography>
            </TableCell>

            <TableCell>
              <ScoreBar score={assessment.score ?? 0} />
            </TableCell>

            <CountCell value={run.itemCounts.remediated} />
            <CountCell value={run.itemCounts.generated} />

            <TableCell align="right" sx={{ pr: 2 }}>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}
              >
                <Button
                  variant="secondary"
                  startIcon={<FilesIcon size={16} />}
                  onClick={() => setOpenDocs({ assessment, run })}
                >
                  {t('app.remediation.remediated.viewDocuments')}
                </Button>
                <Button
                  variant="tertiary"
                  startIcon={<ClockCounterClockwiseIcon size={16} />}
                  onClick={() => onOpenAssessment(assessment.id)}
                >
                  {t('app.remediation.remediated.viewAssessment')}
                </Button>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </DataTable>

      {openDocs && (
        <RemediatedDocumentsDialog
          open
          onClose={() => setOpenDocs(null)}
          runId={openDocs.run.runId}
          assessmentTitle={openDocs.assessment.title}
        />
      )}
    </>
  );
});

/**
 * A produced-document count. Zero is shown in tertiary grey rather than dropped:
 * unlike the outcome breakdown, this is a fixed column — an empty cell would
 * read as missing data, where "0" is the answer.
 */
const CountCell: FC<{ value: number }> = observer(({ value }) => {
  const theme = useTheme();
  return (
    <TableCell align="center">
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 600,
          color: value > 0 ? theme.palette.text.primary : theme.palette.text.tertiary,
        }}
      >
        {value}
      </Typography>
    </TableCell>
  );
});
